import { useEffect, useRef, useCallback } from 'react'

import {
  usePlaybackState,
  usePlaybackMetrics,
  setPlaybackState,
  removePlaybackState,
  setPlaybackMetrics,
  updatePlaybackMetrics,
} from '../../../firebase/hooks/useVideoPlayback'
import { useLiveId } from '../../../global/blocs/roomInfo'
import {
  publishTrack,
  unpublishAll,
  useRoomStatus,
} from '../../../webrtc/webRTCRoom'

interface HTMLVideoElementWithCaptureStream extends HTMLVideoElement {
  captureStream(): MediaStream
}

export const useOnDemandPlayback = () => {
  const liveId = useLiveId()
  const videoElementRef = useRef<HTMLVideoElementWithCaptureStream>(null)
  const currentVideoUrlRef = useRef<string | null>()
  const playbackState = usePlaybackState()
  const prevPlaybackStateRef = useRef<typeof playbackState | null>()
  const playbackMetrics = usePlaybackMetrics()
  // const { publishAudioTrack, publishVideoTrack, unpublishCurrentTracks } =
  //   useWebinarVideo()
  const roomStatus = useRoomStatus()

  /** Effect that makes sure to store the previous playback state */
  useEffect(() => {
    prevPlaybackStateRef.current = playbackState
  })

  /**
   * Effect that triggers if the rooms disconnects
   *
   * Setting the current video url ref and previous playback state ref to null
   *  will make sure that headless can recover itself when reconnecting.
   */
  useEffect(() => {
    if (roomStatus === 'disconnected') {
      currentVideoUrlRef.current = null
      prevPlaybackStateRef.current = null
    }
  }, [roomStatus])

  const { state: newState, statePayload: payload } = playbackState ?? {}
  const { state: prevState } = prevPlaybackStateRef.current ?? {}

  /** Effect that setup listeners on the video element */
  useEffect(() => {
    const { current: videoElement } = videoElementRef

    if (!videoElement) return undefined

    const onEnded = () => {
      if (playbackState) setPlaybackState('ended')
    }

    const onTimeUpdate = () => {
      updatePlaybackMetrics('position', videoElement.currentTime)
    }

    videoElement.addEventListener('timeupdate', onTimeUpdate)
    videoElement.addEventListener('ended', onEnded)

    return () => {
      videoElement.removeEventListener('timeupdate', onTimeUpdate)
      videoElement.removeEventListener('ended', onEnded)
    }
  }, [playbackState])

  /** */
  const prepareVideoElement = useCallback(
    (onReady) => {
      if (!payload || !videoElementRef.current) return

      // Get video playback url.
      const { url } = payload

      // Get the start position.
      const startPosition = payload?.startPosition || 0

      // Get the updated position if the video playback have been running.
      const position = playbackMetrics?.position

      // Get the current time.
      const currentTime = position || startPosition

      if (url !== currentVideoUrlRef.current) {
        // Update the ref to the current video url.
        currentVideoUrlRef.current = url

        // Set the video element src url.
        videoElementRef.current.src = url

        // Set the video element current time.
        videoElementRef.current.currentTime = currentTime

        // Setup listener for when the content is ready to play through without
        //  buffering, based on an estimation by the browser.
        videoElementRef.current.oncanplaythrough = () => {
          if (!videoElementRef.current) return

          // Capture stream from video element.
          const stream = videoElementRef.current.captureStream()

          // Publish video and audio track.
          const tracks = stream.getTracks()
          const audioTrack = tracks.find((track) => track.kind === 'audio')
          const videoTrack = tracks.find((track) => track.kind === 'video')

          if (audioTrack) publishTrack(audioTrack, 'screenAudio')
          if (videoTrack) publishTrack(videoTrack, 'screen')

          // Call the callback, if provided
          if (onReady) {
            onReady()
          }

          // Reset listener so it can't be called again.
          videoElementRef.current.oncanplaythrough = () => {}
        }
      } else {
        // Call the callback, if provided

        if (onReady) {
          onReady()
        }
      }
    },
    [payload, playbackMetrics]
  )

  const isStateDistinct = useCallback(
    (state) => state === newState && state !== prevState,
    [newState, prevState]
  )

  /** Effect that triggers when the playback receives a play intent. */
  useEffect(() => {
    // Is the state distinct.
    if (isStateDistinct('play_intent_sent')) {
      // Prepare video element (or recover).
      prepareVideoElement(() => {
        if (!payload || !videoElementRef.current) return
        // Set the playback metrics.
        const { duration } = videoElementRef.current
        const position = payload.startPosition
        setPlaybackMetrics({ duration, position })

        videoElementRef.current.currentTime = position

        // Set the playback state.
        setPlaybackState('headless_play_ready')
      })
    }
  }, [isStateDistinct, prepareVideoElement, payload, prevState, liveId])

  /** Effect that triggers when a producer is ready to play. */
  useEffect(() => {
    // Is the state distinct.
    if (isStateDistinct('producer_play_ready')) {
      // Prepare video element (or recover).
      prepareVideoElement(() => {
        // Set the playback state.
        setPlaybackState('playing')
      })
    }
  }, [isStateDistinct, prepareVideoElement])

  /** Effect that triggers when the video playback is playing. */
  useEffect(() => {
    // Is the state district.
    if (isStateDistinct('playing')) {
      // Prepare video element (or recover).
      prepareVideoElement(() => {
        if (!videoElementRef.current) return
        // Play the video.
        videoElementRef.current.play().catch(() => {
          // nothing
        })
      })
    }
  }, [isStateDistinct, prepareVideoElement, liveId])

  /** Effect that triggers when a pause intent is received. */
  useEffect(() => {
    // Is the state distinct.
    if (isStateDistinct('pause_intent_sent')) {
      // Prepare video element (or recover).
      prepareVideoElement(() => {
        // Set the playback state.
        setPlaybackState('paused')
      })
    }
  }, [isStateDistinct, prepareVideoElement])

  /**
   * Effect that triggers when a video playback is paused
   */
  useEffect(() => {
    // Is the state distinct.
    if (isStateDistinct('paused')) {
      // Prepare video element (or recover).
      prepareVideoElement(() => {
        if (!videoElementRef.current) return
        // Pause the video
        videoElementRef.current.pause()
      })
    }
  }, [isStateDistinct, playbackMetrics, prepareVideoElement, liveId])

  /**
   * Effect that triggers when a resume intent is received.
   */
  useEffect(() => {
    // Is the state distinct.
    if (isStateDistinct('resume_intent_sent')) {
      // Prepare video element (or recover).
      prepareVideoElement(() => {
        // Set the playback state.
        setPlaybackState('playing')
      })
    }
  }, [isStateDistinct, prepareVideoElement, liveId])

  /**
   * Effect that triggers when the playback receives a scrub intent.
   */
  useEffect(() => {
    // Is the state distinct.
    if (isStateDistinct('scrub_intent')) {
      // Prepare video element (or recover).
      prepareVideoElement(() => {
        if (!payload || !payload.newPosition || !videoElementRef.current) return
        // Set the new position to scrub to.
        videoElementRef.current.currentTime = payload.newPosition

        // Set the playback state to the previous state. If headless is recover-
        //  ing from a refresh or reconnect, there is no previous state. Making
        //  a choice and pausing the playback.
        setPlaybackState(prevState || 'paused')
      })
    }
  }, [isStateDistinct, prepareVideoElement, payload, prevState, liveId])

  /**
   * Effect that triggers when the playback is ended.
   */
  useEffect(() => {
    // Is the state distinct.
    if (isStateDistinct('ended')) {
      // Prepare video element (or recover).
      prepareVideoElement(() => {
        if (!videoElementRef.current) return
        // Pause the video.
        videoElementRef.current.pause()
      })
    }
  }, [isStateDistinct, prepareVideoElement, liveId])

  /**
   * Effect that triggers when the playback receives a stop intent.
   */
  useEffect(() => {
    // Is the state distinct.
    if (isStateDistinct('stop_intent_sent')) {
      // Unpublish tracks.
      unpublishAll()

      // Remove the playback state in firebase.
      removePlaybackState()

      if (videoElementRef.current) {
        // Reset the source of the video element.
        videoElementRef.current.pause()
        videoElementRef.current.currentTime = 0
        videoElementRef.current.removeAttribute('src')
      }

      // Reset the current video ref
      currentVideoUrlRef.current = null
    }
  }, [isStateDistinct, liveId])

  return { videoElementRef, newState }
}
